import { AxiosResponse } from 'axios';
import { api } from 'hooks/useApi';
import type { UserResponse } from 'types/user';

const verifyUserFromInviteHash = (invite_hash: string) => {
  return api.post(`/auth/invite-code/${invite_hash}/`);
};

interface RegisterInvitedUserPayload {
  inviteCode: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  password?: string;
  /* Used for registering through SSO */
  authToken?: string;
}

const postRegisterInvitedUser = ({
  inviteCode,
  firstName,
  lastName,
  jobTitle,
  password,
  authToken,
}: RegisterInvitedUserPayload) => {
  return api.post(`/auth/register-invited-user/${inviteCode}/`, {
    first_name: firstName,
    last_name: lastName,
    job_title: jobTitle,
    password,
    auth_token: authToken,
  });
};

type CityPermissionObject = {
  city_id: number;
  city_name: string;
};

const getUserCityPermissions = () => {
  return api.get<CityPermissionObject[]>(`/auth/permissions/city/`);
};

const getUserData = (): Promise<AxiosResponse<{ data: UserResponse }>> => {
  return api.get('/user/');
};

export const ERROR_CODES_VALIDATE_TOKEN = {
  INCOMPLETE_SIGNUP: 'INCOMPLETE_SIGNUP',
};

/**
 * Validates the current user token. This is used when we have a token, but are
 * unsure if we have a real user attached to it.
 */
const validateToken = () => {
  return api.post('/validate-token/');
};

const getUser = (userId: number): Promise<AxiosResponse<any>> => {
  return api.get(`/users/${userId}/`);
};

type UserPatchPayload = {
  first_name: string;
  last_name: string;
  timezone: string;
  job_title: string;
};

const patchUser = (
  userId: number,
  data: UserPatchPayload,
): Promise<AxiosResponse<any>> => {
  return api.patch(`/users/${userId}/`, data);
};

type ChangePasswordPayload = {
  current_password: string;
  new_password_1: string;
  new_password_2: string;
};

const postUpdatePassword = (data: ChangePasswordPayload): Promise<AxiosResponse<any>> => {
  return api.post('/auth/update-password/', data);
};

type CreateUserPayload = {
  first_name: string;
  last_name: string;
  email: string;
  job_title: string;
};

const postCreateUser = (data: CreateUserPayload): Promise<AxiosResponse<any>> => {
  return api.post('/users/', data);
};

const getOrganizationUsers = (): Promise<AxiosResponse<any>> => {
  return api.get('/users/');
};

export default {
  postRegisterInvitedUser,
  verifyUserFromInviteHash,
  getUserCityPermissions,
  getUserData,
  validateToken,
  getUser,
  patchUser,
  postUpdatePassword,
  postCreateUser,
  getOrganizationUsers,
};
